import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useWebsites } from '@/context/WebsitesContext';
import ContentView from '@/components/ContentView';
import { useAppState } from '@/context/AppStateContext';
import { useAppChat } from '@/context/AppChatContext';

interface PostTheme {
  id: string;
  subject_matter: string;
  post_digest?: string;
  post_content?: string;
  keywords?: string[];
  status?: string;
  created_at?: string;
  updated_at?: string;
  scheduled_date?: string;
  wp_post_id?: string;
  wp_post_url?: string;
  wp_sent_date?: string;
  image?: string;
}

const Post = () => {
  const { id } = useParams<{ id: string }>();
  const { currentWebsite } = useWebsites();
  const { setCurrentRightPaneUrl } = useAppState();
  const { startEditingPost, stopEditingPost } = useAppChat();
  const [postTheme, setPostTheme] = useState<PostTheme | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id || !currentWebsite?.id) return;

    const fetchPostTheme = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('post_themes')
          .select('*')
          .eq('id', id)
          .eq('website_id', currentWebsite.id)
          .single();

        if (error) {
          console.error('Error fetching post theme:', error);
          setError('Failed to load post');
          return;
        }

        if (!data) {
          setError('Post not found');
          return;
        }

        setPostTheme(data);
      } catch (err) {
        console.error('Error fetching post theme:', err);
        setError('Failed to load post');
      } finally {
        setLoading(false);
      }
    };

    fetchPostTheme();
  }, [id, currentWebsite?.id]);

  // Cleanup effect to stop editing when component unmounts
  useEffect(() => {
    return () => {
      stopEditingPost();
    };
  }, [stopEditingPost]);

  const handleClose = () => {
    stopEditingPost(); // Stop editing when leaving the post
    setCurrentRightPaneUrl('/planned-content');
  };

  const handleDelete = async () => {
    if (!postTheme?.id) return;

    try {
      const { error } = await supabase
        .from('post_themes')
        .delete()
        .eq('id', postTheme.id);

      if (error) {
        console.error('Error deleting post:', error);
        return;
      }

      // Navigate back to planned content
      setCurrentRightPaneUrl('/planned-content');
    } catch (err) {
      console.error('Error deleting post:', err);
    }
  };

  const handleRegenerate = async () => {
    // Start editing mode in chat for regeneration
    if (postTheme) {
      startEditingPost({
        id: postTheme.id,
        title: postTheme.subject_matter,
        content: postTheme.post_content || ''
      });
    }
  };

  const handleGenerateImage = async () => {
    // TODO: Implement image generation logic
    console.log('Generate image for post:', postTheme?.id);
  };

  const handleDeleteImage = async () => {
    if (!postTheme?.id) return;

    try {
      const { error } = await supabase
        .from('post_themes')
        .update({ image: null })
        .eq('id', postTheme.id);

      if (error) {
        console.error('Error deleting image:', error);
        return;
      }

      // Update local state
      setPostTheme(prev => prev ? { ...prev, image: null } : null);
    } catch (err) {
      console.error('Error deleting image:', err);
    }
  };

  const handleSendToWordPress = async () => {
    // TODO: Implement WordPress sending logic
    console.log('Send to WordPress:', postTheme?.id);
  };

  const handleContentUpdate = (newContent: string) => {
    // Update the local post theme state when content is updated via chat
    setPostTheme(prev => prev ? { ...prev, post_content: newContent } : null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading post...</p>
        </div>
      </div>
    );
  }

  if (error || !postTheme) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Post not found'}</p>
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Back to Planned Content
          </button>
        </div>
      </div>
    );
  }

  return (
    <ContentView
      postThemeId={postTheme.id}
      title={postTheme.subject_matter}
      description={postTheme.post_digest}
      fullContent={postTheme.post_content}
      keywords={postTheme.keywords || []}
      dateCreated={postTheme.created_at}
      status={postTheme.status as any}
      wpSentDate={postTheme.wp_sent_date}
      wpPostUrl={postTheme.wp_post_url}
      preview_image_url={postTheme.image}
      onClose={handleClose}
      onDeleteClick={handleDelete}
      onRegenerateClick={handleRegenerate}
      onGenerateImage={handleGenerateImage}
      onDeleteImage={handleDeleteImage}
      onSendToWordPress={handleSendToWordPress}
      onContentUpdate={handleContentUpdate}
    />
  );
};

export default Post;
